import { useCallback, useEffect, useState } from 'react'
import { getTimeAsDayjs, timeZones } from '@forwardflow/helpers/utils'
import { Checkbox, Form, message, Radio, Skeleton, TimePicker } from 'antd'
import dayjs from 'dayjs'
import { useParams } from 'react-router-dom'
import {
   useGetActiveLeadSource,
   useGetActiveLeadSourceDetails,
} from '@forwardflow/hooks/customHooks/campaign/'
import { Button, SelectField } from '@forwardflow/ui'
import CampaignService from '@forwardflow/services/CampaignService'

const DAYS_OF_WEEK = [
   'Monday',
   'Tuesday',
   'Wednesday',
   'Thursday',
   'Friday',
   'Saturday',
   'Sunday',
] as const

export const AppointmentTimeFrames = () => {
   const { title } = useParams()

   const [form] = Form.useForm()
   const { data: appointmentTimeFramesData } = useGetActiveLeadSourceDetails(
      title as string
   )
   const [edit, setEdit] = useState(
      appointmentTimeFramesData?.data?.appointmentTimeFrames ? false : true
   )
   const { mutate: mutateActiveLeadSource } = useGetActiveLeadSource(
      title || ''
   )

   const [isLoading, setIsLoading] = useState(false)
   const [selectedOption, setSelectedOption] = useState('custom')

   const [appointmentTimeFrames, setAppointmentTimeFrames] = useState(() => ({
      days: DAYS_OF_WEEK.map((day) => ({
         day,
         enabled: false,
         startTime: '09:00',
         endTime: '17:00',
      })),
      timezone: '',
      allowAgentsToSetAvailability: false,
   }))

   useEffect(() => {
      if (appointmentTimeFramesData?.data?.appointmentTimeFrames) {
         const receivedDays =
            appointmentTimeFramesData.data.appointmentTimeFrames.days
         setAppointmentTimeFrames((prev) => ({
            ...prev,
            days: DAYS_OF_WEEK.map((day) => {
               const matchingDay = receivedDays.find((d: any) => d.day === day)
               return (
                  matchingDay || {
                     day,
                     enabled: false,
                     startTime: '09:00',
                     endTime: '17:00',
                  }
               )
            }),
            timezone:
               appointmentTimeFramesData.data.appointmentTimeFrames.timezone,
         }))
      }
   }, [appointmentTimeFramesData?.data?.appointmentTimeFrames])

   const handleOkClick = useCallback(async () => {
      setIsLoading(true)
      try {
         await form.validateFields()
         await CampaignService.updateAppointmentTimeFramesData(
            title as string,
            appointmentTimeFrames
         )
         message.success('Appointment Timeframes updated successfully.')
      } catch (error) {
         console.error(
            'An error occurred while updating Appointment Timeframes:',
            error
         )
      } finally {
         setIsLoading(false)
         setEdit(false)
         mutateActiveLeadSource()
      }
   }, [form, appointmentTimeFrames, title])

   const handleCheckboxChange = useCallback(
      (index: number, checked: boolean) => {
         setAppointmentTimeFrames((prevState) => {
            const updatedDays = prevState.days.map((day, i) =>
               i === index ? { ...day, enabled: checked } : day
            )
            return { ...prevState, days: updatedDays }
         })
      },
      []
   )

   const validateTimeChange = (
      currentDay: any,
      newTime: string | null,
      type: 'startTime' | 'endTime'
   ) => {
      const startTime = getTimeAsDayjs(currentDay.startTime)
      const endTime = getTimeAsDayjs(currentDay.endTime)

      message.destroy()

      if (
         type === 'endTime' &&
         startTime &&
         newTime &&
         dayjs(newTime, 'HH:mm').isBefore(startTime)
      ) {
         message.error('End time must be after start time')
         return false
      }

      if (
         type === 'startTime' &&
         endTime &&
         newTime &&
         dayjs(newTime, 'HH:mm').isAfter(endTime)
      ) {
         message.error('Start time must be before end time')
         return false
      }

      return true
   }

   const handleTimeChange = useCallback(
      (index: number, time: any, type: 'startTime' | 'endTime') => {
         setAppointmentTimeFrames((prevState) => {
            const updatedDays = [...prevState.days]
            const currentDay = updatedDays[index]
            const newTime = time ? time.format('HH:mm') : null

            if (!validateTimeChange(currentDay, newTime, type)) {
               return prevState
            }

            updatedDays[index] = { ...currentDay, [type]: newTime || '' }
            return { ...prevState, days: updatedDays }
         })
      },
      []
   )

   const handleTimezoneChange = useCallback(
      (value: string) => {
         setAppointmentTimeFrames((prevState) => ({
            ...prevState,
            timezone: value,
         }))
         form.setFieldsValue({ timezone: value })
      },
      [form]
   )

   useEffect(() => {
      if (appointmentTimeFrames) {
         form.setFieldsValue({ timezone: appointmentTimeFrames.timezone })
      }
   }, [appointmentTimeFrames, form])

   return (
      <div className="flex flex-col flex-1">
         <div className="flex items-center justify-between py-5 pl-10 pr-4 font-medium">
            Appointment Timeframes
         </div>
         <div className="bg-primary/10">
            <p className="px-10 py-5 border-b border-black/20 text-[12px] font-medium text-[#B98BE6]">
               Our Assistants will try to contact the new leads during these
               hours, but will keep the conversation going if the lead responds
               even after these hours.
            </p>
            <div className="flex justify-between py-5">
               <div className="flex flex-col w-full gap-y-6">
                  {edit ? (
                     <Form
                        layout="vertical"
                        form={form}
                        className="text-left text-white px-11"
                        autoComplete="off"
                        initialValues={{
                           timezone: appointmentTimeFrames.timezone,
                        }}
                     >
                        <div className="flex flex-col gap-x-5 sm:flex-row">
                           <div className="flex-1">
                              <p className="mb-2 text-[12px]">
                                 Select Time zone
                              </p>

                              <SelectField
                                 isRequired
                                 name="timezone"
                                 dataTestId="timezone-select"
                                 errorMessage="Please choose a time zone"
                                 placeholder="Time zone"
                                 options={timeZones}
                                 className="max-w-96"
                                 onChange={handleTimezoneChange}
                              />
                           </div>
                           <Button
                              type="submit"
                              className="!rounded max-w-28 float-right !mb-0 !mt-6"
                              isLoading={isLoading}
                              onClick={handleOkClick}
                           >
                              Done
                           </Button>
                        </div>
                        <p className="text-[12px]">
                           Select Business Days and Hours
                        </p>
                        <Radio.Group
                           className="mb-4"
                           value={selectedOption}
                           onChange={(e) => {
                              const value = e.target.value
                              setSelectedOption(value)
                              if (value === 'all-week') {
                                 setAppointmentTimeFrames((prevState) => ({
                                    ...prevState,
                                    days: prevState.days.map((day) => ({
                                       ...day,
                                       enabled: true,
                                    })),
                                 }))
                              } else if (value === 'all-week-except-sunday') {
                                 setAppointmentTimeFrames((prevState) => ({
                                    ...prevState,
                                    days: prevState.days.map((day) => ({
                                       ...day,
                                       enabled: day.day !== 'Sunday',
                                    })),
                                 }))
                              } else if (value === 'week-days') {
                                 setAppointmentTimeFrames((prevState) => ({
                                    ...prevState,
                                    days: prevState.days.map((day) => ({
                                       ...day,
                                       enabled: ![
                                          'Saturday',
                                          'Sunday',
                                       ].includes(day.day),
                                    })),
                                 }))
                              }
                           }}
                        >
                           <div className="my-4">
                              <Radio value="all-week" className="ff-radio">
                                 <span className="block !text-white">
                                    All week
                                 </span>
                              </Radio>
                           </div>
                           <div className="my-4">
                              <Radio
                                 value="all-week-except-sunday"
                                 className="ff-radio"
                              >
                                 <span className="block !text-white">
                                    All week (except sunday)
                                 </span>
                              </Radio>
                           </div>
                           <div className="my-4">
                              <Radio value="week-days" className="ff-radio">
                                 <span className="block !text-white">
                                    Week days (Mon to Fri)
                                 </span>
                              </Radio>
                           </div>
                           <div className="mt-4">
                              <Radio value="custom" className="ff-radio">
                                 <span className="block !text-white">
                                    Custom
                                 </span>
                              </Radio>
                           </div>
                        </Radio.Group>
                        <div className="flex flex-col p-4 border border-white/20 rounded gap-y-2 max-w-[480px]">
                           {appointmentTimeFrames.days.map((day, index) => (
                              <div
                                 key={index}
                                 className="flex items-center justify-between"
                              >
                                 <Checkbox
                                    checked={day.enabled}
                                    onChange={(e) =>
                                       handleCheckboxChange(
                                          index,
                                          e.target.checked
                                       )
                                    }
                                 >
                                    <p className="w-[75px]">{day.day}</p>
                                 </Checkbox>
                                 <TimePicker
                                    format="hh:mm a"
                                    value={
                                       day.enabled
                                          ? getTimeAsDayjs(day.startTime)
                                          : null
                                    }
                                    className="mr-2 ff-time-picker"
                                    disabled={!day.enabled}
                                    showNow={false}
                                    allowClear={true}
                                    onChange={(time) =>
                                       handleTimeChange(
                                          index,
                                          time,
                                          'startTime'
                                       )
                                    }
                                 />
                                 <TimePicker
                                    format="hh:mm a"
                                    value={
                                       day.enabled
                                          ? getTimeAsDayjs(day.endTime)
                                          : null
                                    }
                                    className="ff-time-picker"
                                    disabled={!day.enabled}
                                    showNow={false}
                                    allowClear={true}
                                    onChange={(time) =>
                                       handleTimeChange(index, time, 'endTime')
                                    }
                                 />
                              </div>
                           ))}
                        </div>
                     </Form>
                  ) : (
                     <>
                        {isLoading ? (
                           <Skeleton active />
                        ) : (
                           <div className="flex flex-col flex-wrap">
                              <div className="flex flex-col gap-3 px-10">
                                 <div className="flex items-center">
                                    <div className="flex items-center text-[14px] font-medium w-36">
                                       Timezone
                                    </div>
                                    <div className="p-2 px-4 rounded bg-[#828282]/10 flex border border-white/10 text-[14px]">
                                       {
                                          timeZones.find(
                                             (item) =>
                                                item.value ===
                                                appointmentTimeFrames?.timezone
                                          )?.label
                                       }
                                    </div>
                                 </div>
                                 {appointmentTimeFrames?.days
                                    .filter(
                                       (item) =>
                                          item.enabled &&
                                          item.startTime &&
                                          item.endTime
                                    )
                                    .map((item) => (
                                       <div
                                          key={item.day}
                                          className="flex items-center"
                                       >
                                          <div className="w-36 flex items-baseline text-[14px] font-medium pt-2">
                                             {item.day.charAt(0).toUpperCase() +
                                                item.day.slice(1)}
                                          </div>
                                          <div className="p-2 px-4 rounded bg-[#828282]/10 flex border border-white/10 text-[14px]">
                                             {dayjs(
                                                item.startTime,
                                                'HH:mm'
                                             ).format('hh:mm a')}{' '}
                                             -{' '}
                                             {dayjs(
                                                item.endTime,
                                                'HH:mm'
                                             ).format('hh:mm a')}
                                          </div>
                                       </div>
                                    ))}
                              </div>
                           </div>
                        )}
                     </>
                  )}
               </div>
               {!edit && (
                  <div className="px-10">
                     <button
                        onClick={() => setEdit(true)}
                        className={`h-10 font-medium rounded bg-white/10 w-28 hover:bg-white/20 transition-all`}
                     >
                        Edit
                     </button>
                  </div>
               )}
            </div>
         </div>
      </div>
   )
}
